import { z } from "zod";

export const MAX_FILE_UPLOAD_COUNT = 25 as const;

const sectionKeyRegex = /^sections\/.+\.(?:liquid|json)$/;

export const sectionAssetSchema = z
  .object({
    key: z.string().regex(sectionKeyRegex),
    public_url: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    content_type: z.string(),
    size: z.number(),
    checksum: z.string(),
    theme_id: z.number(),
  })
  .describe("SectionAsset");

export const sectionSchema = z
  .object({
    key: z.string().regex(sectionKeyRegex),
    themeId: z.string(),
    publicUrl: z.string().nullable(),
    size: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("Section");

export const shopifyAssetSchema = z
  .object({
    id: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    publicUrl: z.string(),
    name: z.string(),
    type: z.enum(["image", "video", "other"]),
    width: z.number().int().nullish(),
    height: z.number().int().nullish(),
    fileSize: z.number().int(),
  })
  .describe("ShopifyAsset");

export const assetTypeSchema = z
  .enum(["image", "video", "font"])
  .describe("AssetType");

export const assetSchema = z
  .object({
    id: z.string(),
    name: z.string().max(255),
    createdAt: z.date(),
    updatedAt: z.date(),
    url: z.string().max(512),
    type: assetTypeSchema,
    sizeBytes: z.number().int(),
    fileExtension: z.string(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    projectId: z.string().uuid(),
    altText: z.string().nullable(),
    embeddingText: z.string().nullable(),
    embedding: z.number().array().nullable().optional(),
  })
  .describe("Asset");

export const assetLabelOptionsSchema = z
  .enum([
    // --- ICONS ---
    "Icon/Trust",
    "Icon/Payment",
    "Icon/Feature",
    "Icon",

    // --- PHOTOS ---
    "Photo/Lifestyle",
    "Photo/Model",
    "Photo/Customer",
    "Photo/UGC",
    "Photo/Influencer",
    "Photo/Before After",
    "Photo",

    // --- PRODUCT ---
    "Product",
    "Product/Nutrition",
    "Product/Size Chart",
    "Product/Ingredients",

    // --- LOGOS ---
    "Logo/Brand",
    "Logo/Press Logo",
  ])
  .describe("AssetLabelOptions");

export type AssetLabelOptions = z.infer<typeof assetLabelOptionsSchema>;

export const assetLabelDescriptions: Record<AssetLabelOptions, string> = {
  // --- ICONS ---
  "Icon/Trust":
    "Icon representing trust signals (e.g., satisfaction guarantees, secure checkout badges, free shipping/returns, certifications).",
  "Icon/Payment":
    "Icons for accepted payment methods (e.g., Visa, Mastercard, PayPal, Afterpay).",
  "Icon/Feature":
    "Icons highlighting specific product benefits or features (e.g., waterproof, Bluetooth enabled, organic).",
  Icon: "Use this tag when there is no other more specific label that fits.",

  // --- PHOTOS ---
  "Photo/Lifestyle":
    "Product shown in a realistic setting or context, illustrating how it fits into a customer's life.",
  "Photo/Model":
    "Photo featuring a model interacting with, wearing, or using the product.",
  "Photo/Customer":
    "Authentic photo provided by a customer, often used in testimonials or community sections.",
  "Photo/UGC":
    "User-generated content; photos sourced from customers showcasing the product in their own way.",
  "Photo/Influencer":
    "Content created by social media influencers or creators featuring the product, often in a UGC style.",
  "Photo/Before After":
    "Comparison image showing the state before and after using the product to demonstrate effectiveness.",
  Photo: "Use this tag when there is no other more specific label that fits.",

  // --- PRODUCT ---
  Product: "Standard image of the product, showing the main item clearly.",
  "Product/Nutrition":
    "Image of the nutrition facts panel, supplement facts, or full ingredient list.",
  "Product/Size Chart":
    "Visual guide showing product dimensions or apparel sizing information.",
  "Product/Ingredients":
    "Close-up or stylized image focusing on the key ingredients of a product.",

  // --- LOGOS ---
  "Logo/Brand": "The primary logo of the brand.",
  "Logo/Press Logo":
    "Logos of media or brands other than the primary brand. This may be from customer lists, press releases, or other sources.",
};
